import type { ContentBlockData } from "./block.types";

/**
 * The grouped `data.map_layer` object the backend attaches to promoted map-layer evidence
 * blocks (map-layer-block-ui S2, backend `build_evidence_content_blocks` → `_map_layer_data`).
 * On the wire it sits at `UnifiedBlock.value.data.map_layer`, i.e. a key of the block's
 * `ContentBlockData` payload. Key semantics (user contract, map-layer-block-ui S2):
 * - `layer_id` — get_layer_geometry lineage, matches `overlay_layers[].id`. Present → the UI
 *   offers UPDATE of that existing layer; null → the UI offers ADD as a new layer (the new id
 *   is UI-generated). NO other field participates in that decision.
 * - `name` — provenance.layer_name → FeatureCollection top-level `name` → null.
 * - `style` — IVectorStyle flat/rules shape, carried INLINE so the card can preview without a fetch.
 * - `style_metadata` — style legend metadata dict, also inline.
 * Unstyled (`style`/`style_metadata` null) is a normal, expected state — never an error.
 *
 * The block deliberately carries NO `data.layer` annotation (agent-deployment-api
 * `evidence_blocks.py:309-316`: map_layer is intentionally out of `_LAYER_KINDS` — the geometry
 * `LayerType` router is a different mechanism), so the evidence-layer router
 * (`resolveLayerContract`, style router dicts) stays untouched and blocks without `map_layer`
 * keep byte-identical behavior.
 */
export interface MapLayerContract {
  layer_id: string | null;
  name: string | null;
  style: Record<string, unknown> | null;
  style_metadata: Record<string, unknown> | null;
}

/**
 * Resolves the `data.map_layer` contract off a content block's data payload
 * (map-layer-block-ui S2). Reads ONLY the `map_layer` key — no `data.label`/metadata/source
 * fallback (S8: the UI layer name never comes from `data.label`). Rules:
 * - `map_layer` absent (or not a plain object) → `undefined`: the block renders exactly as today.
 * - `layer_id`/`name` pass through as strings; any other shape (missing, null, non-string) is
 *   absent-data → `null`, never an error.
 * - `style`/`style_metadata` must be plain objects (arrays are not); any other shape → `null`.
 */
export function resolveMapLayerContract(block: ContentBlockData): MapLayerContract | undefined {
  const raw: unknown = (block as ContentBlockData | null | undefined)?.map_layer;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  return {
    layer_id: asNullableString(record["layer_id"]),
    name: asNullableString(record["name"]),
    style: asNullableRecord(record["style"]),
    style_metadata: asNullableRecord(record["style_metadata"]),
  };
}

/** Strings pass through; every other shape (missing, null, non-string) is absent-data → null. */
function asNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** Plain objects pass through (arrays are not); every other shape is absent-data → null. */
function asNullableRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
